/**
 * Helpers for saving canvas selection as text / element templates.
 * Element templates keep the FULL selection (text + images + shapes + …).
 * Text templates filter to text nodes only for the Text side panel.
 */
import type { RaeditorStore } from '@reyka/openpolotno';

export type CanvasElementLike = {
  id?: string;
  type?: string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  fontSize?: number;
  toJSON?: () => Record<string, unknown>;
  [key: string]: unknown;
};

export type PresetJson = {
  width: number;
  height: number;
  fonts?: unknown[];
  pages: Array<{
    id: string;
    width: string;
    height: string;
    background: string;
    bleed: number;
    children: Record<string, unknown>[];
  }>;
};

export { apiBase, withApiKey } from './apiAuth';

/** Resolve currently selected elements from the OpenPolotno store. */
export function getSelectedElements(store: RaeditorStore): CanvasElementLike[] {
  const selected = (store as { selectedElements?: CanvasElementLike[] }).selectedElements;
  if (Array.isArray(selected) && selected.length > 0) {
    return selected;
  }

  const ids = (store as { selectedElementsIds?: string[] }).selectedElementsIds;
  if (!Array.isArray(ids) || ids.length === 0) return [];

  const getById = (store as { getElementById?: (id: string) => CanvasElementLike | undefined })
    .getElementById;
  if (typeof getById === 'function') {
    return ids.map((id) => getById(id)).filter(Boolean) as CanvasElementLike[];
  }

  // Fallback: scan pages
  const pages = (store.pages || []) as Array<{ children?: CanvasElementLike[] }>;
  const out: CanvasElementLike[] = [];
  const idSet = new Set(ids);
  for (const page of pages) {
    for (const child of page.children || []) {
      if (child?.id && idSet.has(String(child.id))) out.push(child);
    }
  }
  return out;
}

export function isTextElement(el: CanvasElementLike): boolean {
  return String(el?.type || '').toLowerCase() === 'text';
}

export function filterTextElements(elements: CanvasElementLike[]): CanvasElementLike[] {
  return elements.filter(isTextElement);
}

/** True when every selected element is text (and at least one). */
export function isTextOnly(elements: CanvasElementLike[]): boolean {
  return elements.length > 0 && elements.every(isTextElement);
}

function elementToPlain(el: CanvasElementLike): Record<string, unknown> {
  let raw: Record<string, unknown>;
  if (typeof el.toJSON === 'function') {
    raw = el.toJSON();
  } else {
    raw = { ...el };
  }
  // Strip identity / runtime fields so insert creates fresh nodes
  const clone = JSON.parse(JSON.stringify(raw)) as Record<string, unknown>;
  delete clone.id;
  delete clone.name;
  // Nested children (groups) — strip ids recursively
  const stripNested = (node: Record<string, unknown>) => {
    delete node.id;
    const kids = node.children;
    if (Array.isArray(kids)) {
      for (const k of kids) {
        if (k && typeof k === 'object') stripNested(k as Record<string, unknown>);
      }
    }
  };
  stripNested(clone);
  return clone;
}

function num(v: unknown, fallback = 0): number {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * Build a text/element preset from selected nodes.
 * Positions are normalized so min(x,y) → 0 (relative group).
 * Does NOT filter by type — callers pass the desired subset.
 */
export function buildPresetFromElements(elements: CanvasElementLike[]): PresetJson {
  if (!elements.length) {
    return {
      width: 100,
      height: 100,
      fonts: [],
      pages: [
        {
          id: 'preset-page',
          width: 'auto',
          height: 'auto',
          background: 'transparent',
          bleed: 0,
          children: [],
        },
      ],
    };
  }

  const plains = elements.map(elementToPlain);

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const p of plains) {
    const x = num(p.x);
    const y = num(p.y);
    const w = Math.max(num(p.width, 1), 1);
    const h = Math.max(num(p.height, 1), 1);
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x + w);
    maxY = Math.max(maxY, y + h);
  }

  if (!Number.isFinite(minX)) {
    minX = 0;
    minY = 0;
    maxX = 100;
    maxY = 100;
  }

  const children = plains.map((p) => ({
    ...p,
    x: num(p.x) - minX,
    y: num(p.y) - minY,
  }));

  const width = Math.max(1, Math.ceil(maxX - minX));
  const height = Math.max(1, Math.ceil(maxY - minY));

  return {
    width,
    height,
    fonts: [],
    pages: [
      {
        id: 'preset-page',
        width: 'auto',
        height: 'auto',
        background: 'transparent',
        bleed: 0,
        children,
      },
    ],
  };
}

/**
 * Insert a saved preset onto the active page (same scaling as OpenPolotno Text panel).
 */
export function insertPresetOntoCanvas(
  store: RaeditorStore,
  preset: PresetJson,
  opts?: { select?: boolean },
): string[] {
  const page = store.activePage as
    | { addElement?: (attrs: Record<string, unknown>) => { id?: string } }
    | null
    | undefined;
  if (!page || typeof page.addElement !== 'function') return [];

  const storeW = typeof store.width === 'number' ? store.width : 1080;
  const storeH = typeof store.height === 'number' ? store.height : 1080;
  const scale = (storeW + storeH) / 2160;
  const presetW = num(preset.width, 100);
  const presetH = num(preset.height, 100);
  const baseX = storeW / 2 - (presetW / 2) * scale;
  const baseY = storeH / 2 - (presetH / 2) * scale;

  const children = preset.pages?.[0]?.children || [];
  const ids: string[] = [];

  const history = store.history as { transaction?: (fn: () => void) => void } | undefined;
  const run = (fn: () => void) => {
    if (typeof history?.transaction === 'function') history.transaction(fn);
    else fn();
  };

  run(() => {
    for (const child of children) {
      const attrs = { ...child };
      delete attrs.id;
      const next: Record<string, unknown> = {
        ...attrs,
        x: num(attrs.x) * scale + baseX,
        y: num(attrs.y) * scale + baseY,
        width: num(attrs.width, 1) * scale,
        height: num(attrs.height, 1) * scale,
      };
      if (typeof attrs.fontSize === 'number') {
        next.fontSize = attrs.fontSize * scale;
      }
      const added = page.addElement!(next);
      if (added?.id) ids.push(String(added.id));
    }
    if (opts?.select !== false && ids.length) {
      const select = (store as { selectElements?: (ids: string[]) => void }).selectElements;
      if (typeof select === 'function') select(ids);
    }
  });

  return ids;
}

const PLACEHOLDER_PREVIEW =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAgAAAAICAYAAADED76LAAAAFElEQVQYV2P8z8BQz0AEYBxVSF+FABJADveWkH+aAAAAAElFTkSuQmCC';

/** Capture a preview for the selection (MVP: full active page via toDataURL). */
export async function captureSelectionPreview(store: RaeditorStore): Promise<string> {
  const activeId =
    (store.activePage && typeof store.activePage === 'object' && 'id' in store.activePage
      ? String((store.activePage as { id?: string }).id || '')
      : '') || undefined;

  try {
    if (typeof store.toDataURL === 'function') {
      for (const pixelRatio of [0.5, 1]) {
        const opts: { mimeType: string; pixelRatio: number; pageId?: string } = {
          mimeType: 'image/png',
          pixelRatio,
        };
        if (activeId) opts.pageId = activeId;
        const url = await store.toDataURL(opts);
        if (typeof url === 'string' && url.startsWith('data:image/')) return url;
      }
    }
  } catch (err) {
    console.warn('captureSelectionPreview failed', err);
  }
  return PLACEHOLDER_PREVIEW;
}
